import * as React from 'react';

import {
  Typography,
  Button,
  Modal,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Switch,
  TextField,
  Stack,
  IconButton,
  Divider,
  Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ALLOWED_MESSAGE_TYPES } from 'app/pages/TemplateGenerationSettingsComponent/data/constants';
import type {
  EmailTemplateSegmentConfig,
} from 'app/pages/TemplateGenerationSettingsComponent/index';
import { useTemplateGenerationModalState } from 'app/pages/TemplateGenerationSettingsComponent/hooks/useTemplateGenerationModalState';

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 560,
  maxWidth: '90vw',
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  boxShadow: 24,
  p: 3,
  maxHeight: '90vh',
  overflow: 'auto',
};

export interface SegmentOption {
  _id: string;
  name?: string;
  segment_name?: string;
}

export type FieldValueByFieldName = Record<string, string[]>;
export type FieldNamesBySegment = Record<string, string[]>;

interface TemplateGenerationModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  initialValues?: EmailTemplateSegmentConfig | null;
  segments: SegmentOption[];
  segmentsLoading?: boolean;
  onSave: (config: EmailTemplateSegmentConfig) => void;
  saving?: boolean;
  fieldValueByFieldName?: FieldValueByFieldName;
  fieldNamesBySegment?: FieldNamesBySegment;
}

export const TemplateGenerationModal = ({
  open,
  onClose,
  mode,
  initialValues,
  segments,
  segmentsLoading,
  onSave,
  saving,
  fieldValueByFieldName = {},
  fieldNamesBySegment = {},
}: TemplateGenerationModalProps) => {
  const {
    form,
    availableFieldNames,
    getFieldValueOptions,
    handleSegmentChange,
    handleSegmentIdChange,
    handleSegmentNameChange,
    handleMessageTypeChange,
    handleAutoApprovalChange,
    handleConditionFieldNameChange,
    handleConditionFieldValueChange,
    handleAddConditionRow,
    handleRemoveCondition,
    handleSubmit,
  } = useTemplateGenerationModalState({
    open,
    mode,
    initialValues,
    segments,
    segmentsLoading,
    onSave,
    fieldValueByFieldName,
    fieldNamesBySegment,
  });

  const segmentName = React.useCallback(
    (s: SegmentOption) => s.name ?? s.segment_name ?? s._id,
    [],
  );

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="template-generation-modal">
      <Box sx={modalStyle}>
        <Typography id="template-generation-modal" variant="h6" component="h2" gutterBottom>
          {mode === 'add' ? 'Add Configuration' : 'Edit Configuration'}
        </Typography>

        {mode === 'add' ? (
          segments.length > 0 ? (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Segment</InputLabel>
              <Select
                value={form.segment_id}
                label="Segment"
                onChange={e => handleSegmentChange(String(e.target.value))}
                disabled={segmentsLoading}
              >
                {segments.map(s => (
                  <MenuItem key={s._id} value={s._id}>
                    {segmentName(s)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Stack direction="column" spacing={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Segment ID"
                value={form.segment_id}
                onChange={e => handleSegmentIdChange(e.target.value)}
                disabled={segmentsLoading}
              />
              <TextField
                fullWidth
                label="Segment Name"
                value={form.segment_name}
                onChange={e => handleSegmentNameChange(e.target.value)}
              />
            </Stack>
          )
        ) : (
          <Stack direction="column" spacing={2} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Segment ID"
              value={form.segment_id}
              onChange={e => handleSegmentIdChange(e.target.value)}
            />
            <TextField
              fullWidth
              label="Segment Name"
              value={form.segment_name}
              onChange={e => handleSegmentNameChange(e.target.value)}
            />
          </Stack>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Field Conditions Section */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Field Conditions
        </Typography>

        <Box sx={{ mb: 1.5 }}>
          {(form.field_conditions || []).length > 0 ? (
            <Box>
              {(form.field_conditions || []).map((condition, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.75,
                    px: 1.5,
                    py: 0.75,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id={`field-name-label-${idx}`}>Field Name</InputLabel>
                    <Select
                      labelId={`field-name-label-${idx}`}
                      value={condition.field_name}
                      label="Field Name"
                      displayEmpty
                      onChange={e =>
                        handleConditionFieldNameChange(idx, String(e.target.value))
                      }
                    >
                      <MenuItem value="">—</MenuItem>
                      {availableFieldNames.map(opt => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Autocomplete
                    freeSolo
                    options={getFieldValueOptions(condition.field_name)}
                    value={condition.field_value}
                    onInputChange={(_e, value) =>
                      handleConditionFieldValueChange(idx, value ?? '')
                    }
                    onChange={(_e, value) =>
                      handleConditionFieldValueChange(
                        idx,
                        typeof value === 'string' ? value : value ?? '',
                      )
                    }
                    renderInput={params => (
                      <TextField {...params} size="small" label="Field Value" />
                    )}
                    sx={{ flex: 1, minWidth: 180 }}
                  />

                  <IconButton
                    size="small"
                    onClick={() => handleRemoveCondition(idx)}
                    aria-label="remove condition"
                    color="error"
                    sx={{ flex: '0 0 auto' }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No field conditions configured.
            </Typography>
          )}

          <Button
            variant="outlined"
            onClick={handleAddConditionRow}
            startIcon={<AddIcon />}
            sx={{ whiteSpace: 'nowrap', height: 40, mt: 1 }}
          >
            Add Row
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Allowed Message Types
        </Typography>
        <FormGroup row sx={{ mb: 2 }}>
          {ALLOWED_MESSAGE_TYPES.map(type => (
            <FormControlLabel
              key={type}
              control={
                <Checkbox
                  checked={(form.allowed_message_types || []).includes(type)}
                  onChange={e => handleMessageTypeChange(type, e.target.checked)}
                  name={type}
                />
              }
              label={type.replace(/_/g, ' ')}
            />
          ))}
        </FormGroup>

        <FormControlLabel
          control={
            <Switch
              checked={form.auto_approval}
              onChange={e => handleAutoApprovalChange(e.target.checked)}
            />
          }
          label="Auto Approval"
          sx={{ mb: 2, display: 'block' }}
        />

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};
